import Button from '@components/V4/Button';
import { useAuthState } from '@contexts/AuthContext';
import apiClient from '@contexts/AuthContext/apiClient';
import { enrollmentsApis } from '@shared/apis';
import { MentorAIFeedback } from '@shared/clientTypes/DTOs/EnrollmentsDTO';
import { MentorFeedback, ParentFeedback, StudentFeedback } from '@shared/common';
import { UserRole } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import { useState } from 'react';
import SessionModalEmptyState from '../../../DetailsModalEmptyState';
import MentorAIFeedbackAccordion from './MentorAIFeedbackAccordion';
import MentorAIRecommendationAccordion from './MentorAIRecommendationAccordion';
import MentorFeedbackAccordion from './MentorFeedbackAccordion';
import ParentFeedbackAccordion from './ParentFeedbackAccordion';
import StudentFeedbackAccordion from './StudentFeedbackAccordion';

interface SessionFeedbackTabProps {
  sessionId?: string;
  hasTranscript?: boolean;
  mentorFeedback?: MentorFeedback;
  mentorAIFeedback?: MentorAIFeedback;
  studentFeedback?: StudentFeedback[];
  parentFeedback?: ParentFeedback;
  mentorRecommendation?: string;
}

const SessionFeedbackTab = (props: SessionFeedbackTabProps) => {
  const {
    sessionId,
    hasTranscript,
    mentorFeedback,
    mentorAIFeedback,
    studentFeedback,
    parentFeedback,
    mentorRecommendation,
  } = props;

  const { roles } = useAuthState();
  const toast = useToast();
  const [retryState, setRetryState] = useState<'idle' | 'loading' | 'queued'>('idle');

  const isSuperUser = roles?.includes(UserRole.SuperUser);
  const aiFailed = mentorAIFeedback?.generationFailed === true;
  const aiMissing = !mentorAIFeedback?.sessionSummary;
  const canRetry =
    isSuperUser &&
    !!sessionId &&
    !mentorFeedback?.sessionSummary &&
    (aiFailed || (aiMissing && hasTranscript));

  const retryHandler = async () => {
    if (!sessionId || retryState === 'loading') return;
    setRetryState('loading');
    try {
      const { data } = await apiClient.post(
        enrollmentsApis.retrySessionAiFeedback.endpoint.replace(':sessionId', sessionId),
        {}
      );
      setRetryState('queued');
      toast.success(
        `AI feedback generation queued from the ${
          data.source === 'recorder' ? 'recorder' : 'Zoom'
        } transcript. It usually takes a few minutes; reopen the session to see it.`
      );
    } catch (error) {
      setRetryState('idle');
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      toast.error(message ?? 'The AI feedback generation could not be queued.');
    }
  };

  return (
    <div>
      {canRetry && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-surfaceObject px-4 py-3">
          <span className="text-xs text-customGrey">
            {aiFailed
              ? 'The AI feedback generation failed for this session.'
              : 'No AI feedback was generated for this session, although a transcript exists.'}
          </span>
          <Button
            variant="secondary"
            size="normal"
            buttonText={retryState === 'queued' ? 'Queued' : 'Retry AI feedback generation'}
            isLoading={retryState === 'loading'}
            disabled={retryState === 'queued'}
            onClick={retryHandler}
          />
        </div>
      )}
      {!mentorFeedback?.sessionSummary &&
      !studentFeedback?.length &&
      !mentorAIFeedback &&
      !mentorRecommendation &&
      !parentFeedback?.rating ? (
        <SessionModalEmptyState />
      ) : (
        <div className="flex flex-col gap-1">
          <MentorFeedbackAccordion
            mentorFeedback={mentorFeedback}
            defaultIsAccordionOpen={!!mentorFeedback?.sessionSummary}
          />

          <StudentFeedbackAccordion
            studentFeedback={studentFeedback}
            defaultIsAccordionOpen={
              !mentorFeedback?.sessionSummary && !!studentFeedback?.[0]?.message
            }
          />

          <ParentFeedbackAccordion
            parentFeedback={parentFeedback}
            defaultIsAccordionOpen={!!parentFeedback?.message}
          />

          <MentorAIFeedbackAccordion
            mentorAIFeedback={mentorAIFeedback}
            defaultIsAccordionOpen={
              !mentorFeedback?.sessionSummary &&
              !studentFeedback?.[0]?.message &&
              !!mentorAIFeedback?.createdAt
            }
          />

          <MentorAIRecommendationAccordion
            mentorRecommendation={mentorRecommendation}
            defaultIsAccordionOpen={
              !mentorFeedback?.sessionSummary &&
              !studentFeedback?.[0]?.message &&
              !mentorAIFeedback?.createdAt &&
              !!mentorRecommendation
            }
          />
        </div>
      )}
    </div>
  );
};

export default SessionFeedbackTab;
